import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

const IntelligentChatbots = () => {
  return (
    <>
      <SEO 
        title="Intelligent Chatbots - AI-Powered Conversational Solutions"
        description="Deploy intelligent chatbots with advanced NLP, multi-channel support, and seamless integration. Transform customer engagement with 24/7 automated support that understands context and intent."
        keywords="intelligent chatbots, AI chatbot, conversational AI, NLP chatbot, customer service automation, virtual assistant, chat automation, enterprise chatbot solutions"
      />

      {/* Hero Section */}
      <section className="hero-section relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blush-light/20 to-alabaster"></div>
        <div className="page-container relative">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block mb-4">
              <span className="px-4 py-2 bg-primary-red/10 text-primary-red rounded-full text-sm font-semibold">
                AI Hub / Intelligent Chatbots
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-charcoal animate-fade-in">
              Intelligent <span className="text-gradient">Chatbots</span> That Understand
            </h1>
            <p className="text-xl text-titanium leading-relaxed mb-8 animate-slide-up">
              Deploy AI-powered conversational agents that deliver personalized, context-aware interactions across all customer touchpoints. Transform engagement with chatbots that learn, adapt, and evolve.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up" style={{ animationDelay: '0.1s' }}>
              <Link to="/contact" className="btn-primary">
                Request Demo
              </Link>
              <Link to="/ai-hub" className="btn-secondary">
                Explore AI Hub
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Key Statistics */}
      <section className="py-12 bg-white border-b border-platinum">
        <div className="page-container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-primary-red mb-2">24/7</div>
              <div className="text-sm text-titanium">Availability</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-primary-red mb-2">85%</div>
              <div className="text-sm text-titanium">Query Resolution</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-primary-red mb-2">60%</div>
              <div className="text-sm text-titanium">Cost Reduction</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-primary-red mb-2">3x</div>
              <div className="text-sm text-titanium">Faster Response</div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Capabilities */}
      <section className="py-16 md:py-20 bg-alabaster">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="section-title">Advanced Capabilities</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              Enterprise-grade chatbot features powered by cutting-edge natural language processing and machine learning
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
                title: 'Natural Language Understanding',
                description: 'Advanced NLP that comprehends intent, sentiment, and context across multiple languages with human-like accuracy.'
              },
              {
                icon: 'M13 10V3L4 14h7v7l9-11h-7z',
                title: 'Instant Response Time',
                description: 'Sub-second response times with intelligent caching and optimized processing for seamless conversations.'
              },
              {
                icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
                title: 'Sentiment Analysis',
                description: 'Real-time emotion detection to adapt tone and escalate issues when customer frustration is detected.'
              },
              {
                icon: 'M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4',
                title: 'Multi-Channel Support',
                description: 'Consistent experience across web, mobile, messaging apps, voice assistants, and social media platforms.'
              },
              {
                icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
                title: 'Enterprise Security',
                description: 'Bank-grade encryption, compliance with GDPR/HIPAA, and role-based access controls for data protection.'
              },
              {
                icon: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
                title: 'Continuous Learning',
                description: 'Self-improving AI that learns from every interaction to deliver increasingly accurate and helpful responses.'
              }
            ].map((capability, index) => (
              <div key={index} className="card group hover:shadow-xl transition-all duration-300">
                <div className="w-14 h-14 bg-blush-light/40 rounded-xl flex items-center justify-center mb-4 group-hover:bg-primary-red/20 transition-colors duration-300">
                  <svg className="w-7 h-7 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={capability.icon} />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-3 text-charcoal">{capability.title}</h3>
                <p className="text-titanium">{capability.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases by Industry */}
      <section className="py-16 md:py-20 bg-white">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="section-title">Industry Applications</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              Tailored chatbot solutions that address specific industry challenges and compliance requirements
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {[
              {
                industry: 'Healthcare',
                icon: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z',
                useCases: [
                  'Patient appointment scheduling and reminders',
                  'Symptom assessment and triage',
                  'Medication adherence support',
                  'HIPAA-compliant patient data handling'
                ]
              },
              {
                industry: 'E-Commerce & Retail',
                icon: 'M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z',
                useCases: [
                  'Product recommendations and search',
                  'Order tracking and status updates',
                  'Returns and refund processing',
                  'Personalized shopping assistance'
                ]
              },
              {
                industry: 'Financial Services',
                icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
                useCases: [
                  'Account balance and transaction inquiries',
                  'Fraud detection and security alerts',
                  'Loan application assistance',
                  'Investment portfolio insights'
                ]
              },
              {
                industry: 'Travel & Hospitality',
                icon: 'M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
                useCases: [
                  'Booking and reservation management',
                  'Concierge services and local recommendations',
                  'Check-in/check-out automation',
                  'Multilingual customer support'
                ]
              }
            ].map((industry, index) => (
              <div key={index} className="card hover:shadow-xl transition-all duration-300">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-blush-light/40 rounded-lg flex items-center justify-center mr-4">
                    <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={industry.icon} />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-charcoal">{industry.industry}</h3>
                </div>
                <ul className="space-y-2">
                  {industry.useCases.map((useCase, idx) => (
                    <li key={idx} className="flex items-start text-titanium">
                      <svg className="w-5 h-5 text-primary-red mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <span>{useCase}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Architecture */}
      <section className="py-16 md:py-20 bg-alabaster">
        <div className="page-container">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="section-title">Technical Architecture</h2>
              <p className="section-subtitle max-w-3xl mx-auto">
                Built on a scalable, secure foundation with enterprise-grade infrastructure
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="card">
                <h3 className="text-xl font-semibold mb-4 text-charcoal">Core Components</h3>
                <div className="space-y-3">
                  {[
                    { label: 'NLP Engine', detail: 'Transformer-based language models' },
                    { label: 'Dialogue Manager', detail: 'Context-aware conversation flow' },
                    { label: 'Knowledge Base', detail: 'Dynamic information repository' },
                    { label: 'Integration Layer', detail: 'REST/GraphQL APIs, webhooks' }
                  ].map((component, index) => (
                    <div key={index} className="flex justify-between items-center pb-3 border-b border-platinum last:border-0">
                      <span className="font-medium text-charcoal">{component.label}</span>
                      <span className="text-sm text-titanium">{component.detail}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="card">
                <h3 className="text-xl font-semibold mb-4 text-charcoal">Deployment Options</h3>
                <div className="space-y-4">
                  {[
                    { 
                      option: 'Cloud-Hosted', 
                      features: ['Instant deployment', 'Auto-scaling', 'Global CDN'] 
                    },
                    { 
                      option: 'On-Premise', 
                      features: ['Full data control', 'Custom infrastructure', 'Air-gapped options'] 
                    },
                    { 
                      option: 'Hybrid', 
                      features: ['Best of both worlds', 'Flexible data routing', 'Disaster recovery'] 
                    }
                  ].map((deployment, index) => (
                    <div key={index}>
                      <h4 className="font-semibold text-charcoal mb-2">{deployment.option}</h4>
                      <div className="flex flex-wrap gap-2">
                        {deployment.features.map((feature, idx) => (
                          <span key={idx} className="px-3 py-1 bg-blush-light/30 text-primary-red text-xs rounded-full">
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Integration Ecosystem */}
      <section className="py-16 md:py-20 bg-white">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="section-title">Seamless Integration</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              Connect with your existing tools and platforms for a unified workflow
            </p>
          </div>
          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {[
              'Salesforce', 'Zendesk', 'Slack', 'Microsoft Teams',
              'WhatsApp Business', 'Facebook Messenger', 'Shopify', 'SAP',
              'Oracle', 'Google Analytics', 'Twilio', 'AWS'
            ].map((platform, index) => (
              <div key={index} className="card text-center hover:shadow-lg transition-all duration-300">
                <div className="w-12 h-12 bg-cool-grey/10 rounded-lg mx-auto mb-3"></div>
                <p className="font-medium text-charcoal">{platform}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <p className="text-titanium mb-4">Can't find your platform?</p>
            <Link to="/contact" className="text-primary-red font-semibold hover:underline">
              Request Custom Integration →
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-20 bg-alabaster">
        <div className="page-container">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="section-title">Business Impact</h2>
              <p className="section-subtitle max-w-3xl mx-auto">
                Measurable results that drive ROI and transform customer experience
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {[
                {
                  category: 'Cost Efficiency',
                  metrics: [
                    { metric: '60% reduction', detail: 'in customer service costs' },
                    { metric: '80% automation', detail: 'of routine inquiries' },
                    { metric: '3x faster', detail: 'issue resolution time' }
                  ]
                },
                {
                  category: 'Customer Satisfaction',
                  metrics: [
                    { metric: '24/7 availability', detail: 'instant response anytime' },
                    { metric: '95% accuracy', detail: 'in query resolution' },
                    { metric: '40% increase', detail: 'in customer engagement' }
                  ]
                },
                {
                  category: 'Operational Excellence',
                  metrics: [
                    { metric: 'Zero wait time', detail: 'concurrent conversations' },
                    { metric: '99.9% uptime', detail: 'enterprise reliability' },
                    { metric: 'Real-time insights', detail: 'actionable analytics' }
                  ]
                },
                {
                  category: 'Scalability',
                  metrics: [
                    { metric: 'Unlimited scale', detail: 'handle peak traffic effortlessly' },
                    { metric: 'Multi-language', detail: '100+ languages supported' },
                    { metric: 'Global deployment', detail: 'low-latency worldwide' }
                  ]
                }
              ].map((benefit, index) => (
                <div key={index} className="card">
                  <h3 className="text-xl font-semibold mb-4 text-charcoal flex items-center">
                    <span className="w-2 h-2 bg-primary-red rounded-full mr-3"></span>
                    {benefit.category}
                  </h3>
                  <div className="space-y-3">
                    {benefit.metrics.map((item, idx) => (
                      <div key={idx} className="pl-5">
                        <div className="font-semibold text-primary-red">{item.metric}</div>
                        <div className="text-sm text-titanium">{item.detail}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Case Study Highlight */}
      <section className="py-16 md:py-20 bg-white">
        <div className="page-container">
          <div className="max-w-4xl mx-auto">
            <div className="card bg-gradient-to-br from-blush-light/10 to-alabaster border-2 border-primary-red/20">
              <div className="flex items-start mb-4">
                <div className="w-16 h-16 bg-primary-red/10 rounded-xl flex items-center justify-center flex-shrink-0 mr-4">
                  <svg className="w-8 h-8 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-charcoal mb-2">Success Story</h3>
                  <p className="text-titanium mb-4">
                    Global E-Commerce Platform Transforms Customer Service
                  </p>
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-6 mb-6">
                <div className="text-center p-4 bg-white rounded-lg">
                  <div className="text-3xl font-bold text-primary-red mb-1">75%</div>
                  <div className="text-sm text-titanium">Cost Reduction</div>
                </div>
                <div className="text-center p-4 bg-white rounded-lg">
                  <div className="text-3xl font-bold text-primary-red mb-1">10M+</div>
                  <div className="text-sm text-titanium">Conversations/Month</div>
                </div>
                <div className="text-center p-4 bg-white rounded-lg">
                  <div className="text-3xl font-bold text-primary-red mb-1">4.8/5</div>
                  <div className="text-sm text-titanium">Customer Rating</div>
                </div>
              </div>
              <blockquote className="border-l-4 border-primary-red pl-4 italic text-titanium mb-4">
                "The intelligent chatbot solution from Abacies transformed our customer service operations. We're now handling 10x the volume with better satisfaction scores and significant cost savings."
              </blockquote>
              <p className="text-sm text-titanium">
                — Director of Customer Experience, Fortune 500 Retailer
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Getting Started */}
      <section className="py-16 md:py-20 bg-alabaster">
        <div className="page-container">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="section-title">Getting Started is Easy</h2>
            <p className="section-subtitle mb-12">
              From concept to deployment in weeks, not months
            </p>
            <div className="grid md:grid-cols-4 gap-6 mb-12">
              {[
                { step: '1', title: 'Consultation', desc: 'Discuss your needs and goals' },
                { step: '2', title: 'Design', desc: 'Create conversational flows' },
                { step: '3', title: 'Development', desc: 'Build and train your chatbot' },
                { step: '4', title: 'Deploy', desc: 'Launch and monitor performance' }
              ].map((phase, index) => (
                <div key={index} className="relative">
                  <div className="card text-center">
                    <div className="w-12 h-12 bg-primary-red text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-3">
                      {phase.step}
                    </div>
                    <h3 className="font-semibold text-charcoal mb-2">{phase.title}</h3>
                    <p className="text-sm text-titanium">{phase.desc}</p>
                  </div>
                  {index < 3 && (
                    <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                      <svg className="w-6 h-6 text-primary-red" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-primary-red text-white">
        <div className="page-container text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
            Ready to Deploy Intelligent Chatbots?
          </h2>
          <p className="text-xl mb-8 opacity-90 max-w-3xl mx-auto">
            Transform your customer engagement with AI-powered conversational experiences. Schedule a demo to see our intelligent chatbot platform in action.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-white text-primary-red px-8 py-4 rounded-lg text-lg font-semibold hover:bg-pearl-white transition-all duration-300 shadow-lg">
              Schedule Demo
            </Link>
            <Link to="/ai-hub" className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-primary-red transition-all duration-300">
              Explore More AI Solutions
            </Link>
          </div>
        </div>
      </section>

      {/* Related Solutions */}
      <section className="py-16 bg-white">
        <div className="page-container">
          <h3 className="text-2xl font-bold mb-8 text-charcoal text-center">Related AI Capabilities</h3>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Link to="/ai-hub/nlp" className="card hover:shadow-xl transition-all duration-300">
              <h4 className="font-semibold text-charcoal mb-2">Natural Language Processing</h4>
              <p className="text-sm text-titanium mb-3">Advanced text analysis and understanding</p>
              <span className="text-primary-red text-sm font-semibold">Learn More →</span>
            </Link>
            <Link to="/ai-hub/intelligent-automation" className="card hover:shadow-xl transition-all duration-300">
              <h4 className="font-semibold text-charcoal mb-2">Intelligent Automation</h4>
              <p className="text-sm text-titanium mb-3">Automate complex business processes</p>
              <span className="text-primary-red text-sm font-semibold">Learn More →</span>
            </Link>
            <Link to="/ai-hub/predictive-analytics" className="card hover:shadow-xl transition-all duration-300">
              <h4 className="font-semibold text-charcoal mb-2">Predictive Analytics</h4>
              <p className="text-sm text-titanium mb-3">Data-driven forecasting and insights</p>
              <span className="text-primary-red text-sm font-semibold">Learn More →</span>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default IntelligentChatbots;